import { useCallback, useEffect, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useSystemModuleStore } from '@/features/system-modules';
import { useAnnouncementStore } from '@features/announcements/store';
import { CriticalAnnouncementModal } from '@features/announcements/components';
import { useAuthStore } from '@features/authentication/store/authStore';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import { usePublicConfigStore } from '@features/shared/store/publicConfigStore';
import { PushOnboardingBanner } from '@/features/notifications/components/PushOnboardingBanner';
import { useNotificationEventStream } from '@/features/notifications/hooks/useNotificationEventStream';
import { useNotificationStore } from '@/features/notifications/store/notificationStore';
import { FLOATING_PANEL_HOST_ID } from '@/features/notifications/lib/floatingPanelHost';
import { PostingOutcomeToaster } from '@/features/notifications/components/PostingOutcomeToaster';
import { setNotificationNavigator } from '@/features/notifications/lib/notify';
import { useDocumentCaptureStore } from '@/features/document-capture';
import { NotificationPanel } from '@/features/notifications/components/NotificationPanel';
import { RailNotificationBell } from '@/features/notifications/components/RailNotificationBell';
import { primeNotificationsOnOpen } from '@/features/notifications/lib/primeNotifications';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ModuleRail } from '../components/ModuleRail';
import { ModulePanel } from '../components/ModulePanel';
import { RailProfileMenu } from '../components/RailProfileMenu';
import { DOCSCAN_VIEW_PERMISSIONS } from '../lib/menu';
import { useSyncActiveModuleToRoute } from '../hooks/useSyncActiveModuleToRoute';
import { useShellStore } from '../store/shellStore';

export function AppLayout() {
  const fetchAvailability = useSystemModuleStore((s) => s.fetchAvailability);
  const fetchActiveAnnouncements = useAnnouncementStore((s) => s.fetchActive);
  const fetchPublicConfig = usePublicConfigStore((s) => s.fetch);
  const canViewPublicAnnouncements = useAuthStore((s) => s.hasPermission(PERMISSIONS.ANNOUNCEMENT_PUBLIC_VIEW));
  const hydrateNotificationCounts = useNotificationStore((s) => s.hydrateCounts);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const fetchReviewQueue = useDocumentCaptureStore((s) => s.fetchReviewQueue);
  const navigate = useNavigate();

  const panelOpen = useShellStore((s) => s.panelOpen);
  const notifOpen = useShellStore((s) => s.notifOpen);
  const hydrateShellPrefs = useShellStore((s) => s.hydratePrefs);
  const closeNotif = useShellStore((s) => s.closeNotif);
  const toggleNotif = useShellStore((s) => s.toggleNotif);
  const closeNotifRestoringPanel = useShellStore((s) => s.closeNotifRestoringPanel);
  const shellUserId = useAuthStore((s) => s.user?.userId ?? null);

  /* M6 (86d44z2b7) — the bell's DOM node.
     The popover this replaced got Escape-dismissal and focus-return free from Radix and never
     implemented either. An in-flow <aside> is not a Radix layer and inherits neither, so the ref
     lives here — where both the bell and the panel are rendered — and every close path routes
     focus back to it. Without this, dismissing the inbox drops focus onto <body> and a keyboard
     user restarts their tab journey from the top of the page. */
  const bellRef = useRef<HTMLButtonElement>(null);

  const returnFocusToBell = useCallback(() => {
    bellRef.current?.focus();
  }, []);

  /** The bell: toggles, and on the OPEN transition fires the single fetch. */
  const handleBellToggle = useCallback(() => {
    // Read before toggling — this is the transition, and it is the only path that opens the panel.
    if (!useShellStore.getState().notifOpen) primeNotificationsOnOpen();
    toggleNotif();
    // Closing via the BELL leaves the module panel shut (shell state table); focus is already here.
  }, [toggleNotif]);

  /** The panel's OWN close control — the one path that force-opens the module panel behind it. */
  const handlePanelClose = useCallback(() => {
    closeNotifRestoringPanel();
    returnFocusToBell();
  }, [closeNotifRestoringPanel, returnFocusToBell]);

  // M1 — the rail's lit module follows the route. Mounted here and nowhere else.
  useSyncActiveModuleToRoute();

  // M2 — load THIS user's remembered panel state (owner decision 2: remembered per user). Keyed by
  // user because the auth store persists to localStorage, which is shared across tabs and survives
  // a tab closing — a global key would hand the next person to sign in someone else's layout.
  useEffect(() => {
    hydrateShellPrefs(shellUserId);
  }, [hydrateShellPrefs, shellUserId]);

  // M1's state table: Escape closes NOTIFICATIONS ONLY. It must not collapse the module panel —
  // that is a remembered preference, and Escape is not how you change a preference.
  //
  // M6: Escape also RETURNS FOCUS to the bell. Radix did this for the popover; an <aside> does not.
  // Guarded on `notifOpen` so Escape does not yank focus to the bell from anywhere in the app.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (!useShellStore.getState().notifOpen) return;
      closeNotif();
      returnFocusToBell();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closeNotif, returnFocusToBell]);

  // Everything here is a BACKGROUND fetch — the user opened the app, they didn't ask for any of it.
  // None of these may ever surface a toast on failure (86d3va61r); each request is marked
  // `background: true` in its service, and each store keeps its own error field for inline display.
  useEffect(() => {
    // Module availability feeds ModuleGuard. Permission-free endpoint: this used to hit the admin-only
    // module list, which 403'd for every non-admin — the source of the spurious "Access Denied" toast,
    // and the reason ModuleGuard failed open for precisely the users it is meant to block.
    fetchAvailability();
    // Active announcements drive both the sidebar Home badge and the
    // critical-priority post-login modal — fetch once on app load, but only
    // for users with announcement.public_view (the endpoint is now gated too).
    if (canViewPublicAnnouncements) {
      fetchActiveAnnouncements();
    }
    // Public config (currently the Gmail mailbox address used by the AutoDraft review
    // card) is needed before the user navigates to the quotations list.
    fetchPublicConfig();
    // N5 (86d3w2kg2) — hydrate the notification badge. A dedicated /counts call rather than a page:
    // the badge renders in the shell at boot, long before anyone opens the popover, and asking for a
    // page here would drag 50 rows of timeline JSON along with it on every load.
    hydrateNotificationCounts();
  }, [
    fetchAvailability,
    fetchActiveAnnouncements,
    fetchPublicConfig,
    canViewPublicAnnouncements,
    hydrateNotificationCounts,
  ]);

  // M1 (86d44z1g9, must-fix #4) — RE-HOMED FROM `Sidebar.tsx`, which was the only app-wide caller.
  // M2 deletes that component; without this the Document Capture review badge would sit at zero
  // forever and nothing would fail, log, or throw — the badge would simply never appear.
  //
  // Deliberately its OWN effect rather than folded into the background block above: its guard is a
  // permission, so merging it would make a permission change re-trigger the availability,
  // announcement, config and notification-count fetches too.
  //
  // Only users who can see at least one family have a queue to count — everyone else would just
  // be issuing a request that returns nothing.
  const canSeeAnyDocument = DOCSCAN_VIEW_PERMISSIONS.some((p) => hasPermission(p));
  useEffect(() => {
    if (canSeeAnyDocument) void fetchReviewQueue();
  }, [canSeeAnyDocument, fetchReviewQueue]);

  // N8 — hand the toast layer a real router navigation for its Open action. Module-level injection
  // because notify.ts has no hook context; without it Open falls back to a full document reload.
  useEffect(() => {
    setNotificationNavigator(navigate);
  }, [navigate]);

  // N5 — THE single notification SSE connection for the whole app. Mounted here and nowhere else:
  // this is the first app-lifetime stream in the codebase (every other one is page-scoped), and a
  // second mount would be a second EventSource against a ~6-per-origin browser budget.
  useNotificationEventStream();

  // N8 — the ONE place a posting outcome becomes a toast. Beside the stream deliberately: a second
  // mount would have both instances writing the same toast id, so the user would see one toast being
  // fought over rather than two duplicates, which is a stranger bug to diagnose.

  // CSC-295: SW → SPA navigation bridge. When the user clicks a Web Push toast and
  // a DOP tab is already open, the service worker focuses the tab and posts a
  // `csc:navigate` message. We translate that into an in-app React Router
  // navigation so the user lands on the AutoDraft detail without a full reload.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (event: MessageEvent) => {
      if (event.data?.type !== 'csc:navigate') return;
      const url = event.data.url;
      if (typeof url !== 'string' || !url.startsWith('/')) return;
      navigate(url);
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [navigate]);

  return (
    // ⚠️ TooltipProvider at the LAYOUT root, not per page. Radix's Tooltip THROWS without a provider
    // ancestor, and with no error boundary above the outlet that throw white-screens the entire app.
    // Until N7/N8 every tooltip outside the sidebar happened to sit on a page that brought its own
    // provider (the sidebar carries one too — nesting is harmless); the notification surfaces render
    // tooltips on EVERY page, which is what turned the missing global provider into a crash
    // (found 2026-08-25: /notifications rendered white).
    <TooltipProvider>
    {/* M2 (86d44z1p4, must-fix 4) — THIS component owns the shell's flex row. The rail and the
        module panel are in-flow siblings of the content column and PUSH it; the old sidebar was an
        absolutely-positioned overlay with an in-flow spacer, which is why the cutover is a layout
        change here rather than an edit inside a sidebar component. */}
    <div className="flex h-screen bg-background">
      <ModuleRail
        bellSlot={
          <RailNotificationBell ref={bellRef} open={notifOpen} onToggle={handleBellToggle} />
        }
        profileSlot={<RailProfileMenu />}
      />
      {/* Mutually exclusive by construction — the shell state machine never sets both. Rendering
          them as siblings rather than nesting keeps each one's width its own. */}
      {panelOpen && <ModulePanel />}
      {notifOpen && <NotificationPanel onRequestClose={handlePanelClose} />}
      {/* `relative` is load-bearing, not cosmetic: it makes this column the containing block for
          N8's floating recovery panel, which portals in here rather than fixing itself to the
          viewport. Anchored to the viewport it would sit ON TOP of the shell chrome, whose width
          now varies by up to 332px (rail alone, or rail + panel) depending on a per-user
          preference this layout does not own — so a fixed offset would be wrong in most states.
          Anchoring to the content column is correct in all of them without anyone having to plumb
          the panel's state across the app.
          ⚠️ Do not drop `id` or `relative` — N8's recovery panel silently mis-anchors without them. */}
      {/* `shell-main-container` (M8) makes this column an inline-size container so the layouts
          inside it can decide push-vs-overlay on the width they ACTUALLY have. Every breakpoint in
          the app is viewport-keyed, and the viewport does not change when a panel opens — at 1366px
          this column is 1298 / 1034 / 938 depending on which panel is out. */}
      <div
        id={FLOATING_PANEL_HOST_ID}
        className="shell-main-container relative flex-1 flex flex-col min-w-0"
      >
        <PostingOutcomeToaster />
        <PushOnboardingBanner />
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
      {canViewPublicAnnouncements && <CriticalAnnouncementModal />}
    </div>
    </TooltipProvider>
  );
}
